import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import Text from './text';

// Timeline:
//   - Generic, block-based — consumer provides all UI via :node and :content blocks
//   - Accepts any `@items` array (generic `<T>`)
//   - Best for: custom timelines where each node looks different — e.g., career
//     milestones, activity feeds, changelogs
// TimelineTracker (this component):
//   - Data-driven, opinionated — give it `@steps` with `state` and it handles all visuals
//   - Renders colored markers (emerald/indigo/gray), titles, dates, and descriptions
//   - Each step can optionally include an `icon` component for the marker
//   - Best for: status/progress trackers with a fixed completed/active/upcoming
//     pattern — e.g., application progress, loan stages, onboarding flows
// Rule of thumb: if your timeline has a clear "done / in-progress / not-started"
// progression, use TimelineTracker. If each item is unique, use Timeline.

/**
 * State of a timeline step
 */
export type TimelineStepState = 'completed' | 'active' | 'upcoming';

/**
 * Data structure for a timeline step
 */
export interface TimelineStep {
  /** Title of the step */
  title: string;
  /** Optional description */
  description?: string;
  /** State of the step */
  state: TimelineStepState;
  /** Optional date string to display */
  date?: string;
  /** Optional icon component to render inside the step marker */
  icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
}

interface TimelineTrackerSignature {
  Args: {
    /** Array of timeline steps */
    steps: TimelineStep[];
    /** Whether to use compact spacing */
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * TimelineTracker - A vertical timeline with state markers
 *
 * Displays a vertical sequence of steps with visual state indicators
 * (completed, active, upcoming). Useful for application progress,
 * loan processing stages, or onboarding flows.
 *
 * Each step can optionally include an `icon` property (a component reference)
 * that renders inside the marker instead of the default symbol.
 *
 * @example Basic usage
 * ```gts
 * import TimelineTracker from 'tio-ui/components/timeline-tracker';
 *
 * <TimelineTracker @steps={{array
 *   (hash title="Application Submitted" state="completed" date="Jan 15")
 *   (hash title="Under Review" state="active" description="Estimated 2-3 days")
 *   (hash title="Approved" state="upcoming")
 *   (hash title="Funds Disbursed" state="upcoming")
 * }} />
 * ```
 *
 * @example With custom icons
 * ```gts
 * import { PhFileText, PhMagnifyingGlass, PhCheckCircle } from 'ember-phosphor-icons';
 *
 * <TimelineTracker @steps={{array
 *   (hash title="Submitted" state="completed" icon=PhFileText)
 *   (hash title="Under Review" state="active" icon=PhMagnifyingGlass)
 *   (hash title="Approved" state="upcoming" icon=PhCheckCircle)
 * }} />
 * ```
 *
 * @example Compact variant
 * ```gts
 * <TimelineTracker @steps={{this.steps}} @compact={{true}} />
 * ```
 */
const TimelineTracker: TOC<TimelineTrackerSignature> = <template>
  <div data-test-timeline-tracker class="relative" ...attributes>
    {{#each @steps as |step index|}}
      <div data-test-timeline-step class="flex gap-3 {{if @compact 'pb-4' 'pb-6'}} relative">
        {{! Vertical line connector (skip for last item) }}
        {{#unless (isLastItem index @steps)}}
          <div
            data-test-timeline-connector
            class="tio-timeline__connector absolute left-[11px] top-6 bottom-0 w-0.5
              {{if (isStepCompleted step.state) 'tio-timeline__connector--completed'}}"
          ></div>
        {{/unless}}

        {{! Step marker }}
        <div class="relative shrink-0 z-10">
          {{#if (isStepCompleted step.state)}}
            <div
              data-test-timeline-marker
              class="tio-row-center tio-timeline__marker tio-timeline__marker--completed w-6 h-6"
            >
              {{#if step.icon}}
                {{#let step.icon as |StepIcon|}}
                  <StepIcon @size={{14}} class="tio-fg-inverse" />
                {{/let}}
              {{else}}
                <svg
                  class="w-3.5 h-3.5 tio-fg-inverse"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              {{/if}}
            </div>
          {{else if (isStepActive step.state)}}
            <div
              data-test-timeline-marker
              class="tio-row-center tio-timeline__marker tio-timeline__marker--active w-6 h-6"
            >
              {{#if step.icon}}
                {{#let step.icon as |StepIcon|}}
                  <StepIcon @size={{14}} class="tio-fg-inverse" />
                {{/let}}
              {{else}}
                <div class="w-2 h-2 rounded-full tio-bg-white"></div>
              {{/if}}
            </div>
          {{else}}
            <div
              data-test-timeline-marker
              class="tio-timeline__marker w-6 h-6 tio-row-center
                {{if
                  step.icon
                  'tio-timeline__marker--upcoming-icon'
                  'tio-timeline__marker--upcoming'
                }}"
            >
              {{#if step.icon}}
                {{#let step.icon as |StepIcon|}}
                  <StepIcon @size={{14}} class="tio-fg-muted" />
                {{/let}}
              {{/if}}
            </div>
          {{/if}}
        </div>

        {{! Step content }}
        <div class="tio-fill-min pt-0.5">
          <div class="tio-row-between gap-2">
            <div
              data-test-timeline-title
              class="tio-timeline__title
                {{if
                  (isStepCompleted step.state)
                  'tio-timeline__title--completed'
                  (if (isStepActive step.state) 'tio-timeline__title--active' 'tio-fg-muted')
                }}"
            >
              {{step.title}}
            </div>
            {{#if step.date}}
              <Text data-test-timeline-date @variant="muted" @size="sm" class="shrink-0">
                {{step.date}}
              </Text>
            {{/if}}
          </div>
          {{#if step.description}}
            <Text data-test-timeline-description @variant="muted" @size="sm" class="mt-0.5">
              {{step.description}}
            </Text>
          {{/if}}
        </div>
      </div>
    {{/each}}
  </div>
</template>;

export default TimelineTracker;

/** Helper to check if this is the last item in the array */
function isLastItem(index: number, arr: TimelineStep[]) {
  return index === arr.length - 1;
}

/** Helper to check step state */
function isStepCompleted(state: TimelineStepState) {
  return state === 'completed';
}

function isStepActive(state: TimelineStepState) {
  return state === 'active';
}

export type { TimelineTrackerSignature };
